import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <SEO title="文章未找到 | 花生影院" description="您访问的文章不存在或已被删除。" />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/40">
              <i className="ri-article-line text-2xl text-foreground-500"></i>
            </div>
            <p className="text-foreground-400 mb-4">文章未找到</p>
            <Link to="/" className="text-primary-400 hover:text-primary-300 transition-colors text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && (
      a.category === article.category || a.tags.some((t) => article.tags.includes(t))
    ))
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.huahuashengcinema.com/' },
          { '@type': 'ListItem', position: 2, name: '影评资讯', item: 'https://www.huahuashengcinema.com/category/all' },
          { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.huahuashengcinema.com/article/${article.id}` },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        image: article.coverUrl,
        articleBody: article.content.replace(/<[^>]*>/g, '').substring(0, 200),
        publisher: {
          '@type': 'Organization',
          name: '花生影院',
        },
      },
      {
        '@type': 'WebPage',
        name: `${article.title} | 花生影院`,
        url: `https://www.huahuashengcinema.com/article/${article.id}`,
        description: article.excerpt,
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${article.title} | 花生影院`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.huahuashengcinema.com/article/${article.id}`}
        ogType="article"
        ogImage={article.coverUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <nav className="px-4 md:px-6 lg:px-10 py-4 border-b border-background-300/20" aria-label="Breadcrumb">
          <div className="max-w-4xl mx-auto">
            <ol className="flex items-center gap-2 text-xs md:text-sm text-foreground-500">
              <li><Link to="/" className="hover:text-primary-400 transition-colors">首页</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li><Link to="/category/all" className="hover:text-primary-400 transition-colors">影评资讯</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-300 font-medium truncate max-w-[200px]">{article.title}</li>
            </ol>
          </div>
        </nav>

        <article className="px-4 md:px-6 lg:px-10 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <header className="mb-6">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-500/20 text-primary-300 mb-4">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-50 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.readCount.toLocaleString()} 阅读
                </span>
              </div>
            </header>

            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs rounded-full bg-background-200/50 text-foreground-400 border border-background-300/30 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="relative aspect-[16/9] rounded-xl overflow-hidden mb-8 md:mb-10">
              <img
                src={article.coverUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose-custom text-foreground-200 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-300/20">
              <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="rounded-xl overflow-hidden bg-background-100/60 border border-background-300/20 hover:border-primary-500/30 transition-all duration-300">
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img
                          src={related.coverUrl}
                          alt={related.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3 md:p-4">
                        <h4 className="text-sm font-semibold text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-2">
                          {related.title}
                        </h4>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}